import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectAvailabilitySummaryReportData } from '../../../store/ReportsDetailsSlice/selectors';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  clearAvailabilitySummary,
  getAvailabilitySummaryReportData,
} from '../../../store/ReportsDetailsSlice/actions';
import useAvailabilitySummaryReportColumns from './hooks/useAvailabilitySummaryReportColumns';
import useAvailabilitySummaryReportRows from './hooks/useAvailabilitySummaryReportRows';
import { DataGrid } from '@mui/x-data-grid';
import DataNotFound from '../../../components/Reports/DataNotFound';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const AvailabilitySummaryReport = () => {
  const dispatch = useDispatch();
  const { availabilitySummary, isLoadingAvailabilitySummary } = useSelector(
    selectAvailabilitySummaryReportData
  );

  useEffect(() => {
    dispatch(getAvailabilitySummaryReportData());
    return () => dispatch(clearAvailabilitySummary());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useAvailabilitySummaryReportColumns();
  const rows = useAvailabilitySummaryReportRows(availabilitySummary.data);

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Availability Summary Report" />

      {isLoadingAvailabilitySummary ? (
        <Box
          sx={{ minHeight: '30px' }}
          className="w-10 h-10 d-flex justify-content-center align-items-center"
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          {!!rows.length ? (
            <Box mt={4} sx={{ maxWidth: '900px' }}>
              {rows.map((item) => {
                return (
                  <Box key={item.name} mb={4}>
                    <Typography variant="h6" fontWeight={600} mb={2}>
                      {item.name}
                    </Typography>
                    <DataGrid
                      rows={item.rows}
                      columns={columns}
                      disableRowSelectionOnClick
                      disableColumnMenu
                      rowHeight={64}
                      sx={{
                        '& .MuiTablePagination-selectLabel': {
                          margin: '0px',
                          color: '#0000008e',
                        },
                        '& .MuiTablePagination-displayedRows': {
                          margin: '0px',
                        },
                        '& .MuiTablePagination-actions': {
                          color: '#0000008e',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                          color: '#565453',
                        },
                        '& .MuiDataGrid-cell:focus': {
                          outline: 'none',
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                          outline: 'none',
                        },
                      }}
                      slots={{
                        footer: CustomFooter,
                      }}
                      slotProps={{
                        footer: { total: item.total, columns },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          ) : (
            <DataNotFound />
          )}
        </>
      )}
    </Paper>
  );
};

const CustomFooter = ({ total, columns }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        borderTop: '1px solid rgba(224, 224, 224, 1)',
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold',
        minHeight: 52,
      }}
    >
      {columns.map((column, index) => {
        const value = index === 0 ? 'Total' : total[column.field] || '-';

        return (
          <Box
            key={column.field}
            sx={{
              flex: column.flex || 0,
              minWidth: column.minWidth || 'auto',
              maxWidth: column.maxWidth || 'none',
              width: column.width || 'auto',
              padding: '0px 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent:
                column.headerAlign === 'right'
                  ? 'flex-end'
                  : column.headerAlign === 'center'
                    ? 'center'
                    : 'flex-start',
              fontSize: '0.875rem',
            }}
          >
            {value}
          </Box>
        );
      })}
    </Box>
  );
};

export default AvailabilitySummaryReport;
